// pages/strategic-playbook/tools/cascade-mapper.js
// ════════════════════════════════════════════════════
// CASCADE MAPPER
// ════════════════════════════════════════════════════
// Map any shock to YOUR specific situation. 7 visible
// cascade layers + permanence + timing + personal action
// per dimension.
// ════════════════════════════════════════════════════

import { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';

const URGENCY_COLORS = {
  Days: 'var(--sp-oxblood)',
  Weeks: 'var(--sp-cognac)',
  Months: 'var(--sp-aged-brass)',
};

const LAYER_ACCENTS = {
  ended: 'var(--sp-oxblood)',
  valuable: 'var(--sp-forest-moss)',
  worthless: 'var(--sp-oxblood)',
  winner: 'var(--sp-forest-moss)',
  'winner-2': 'var(--sp-sage-olive)',
  loser: 'var(--sp-oxblood)',
  'loser-2': 'var(--sp-cognac)',
};

export default function CascadeMapperTool() {
  const [shock, setShock] = useState('');
  const [careerExposure, setCareerExposure] = useState('');
  const [businessExposure, setBusinessExposure] = useState('');
  const [portfolioExposure, setPortfolioExposure] = useState('');
  const [familyExposure, setFamilyExposure] = useState('');
  const [timeFrame, setTimeFrame] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function generate() {
    if (shock.trim().length < 10) {
      setError('Describe the shock in more detail (at least 10 characters).');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/strategic-playbook/cascade-mapper', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shock,
          careerExposure,
          businessExposure,
          portfolioExposure,
          familyExposure,
          timeFrame,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate');
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sp-root">
      <Head>
        <title>Cascade Mapper · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link href="/strategic-playbook/tools" className="sp-reader-back" style={{ marginBottom: 16 }}>← Tools</Link>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Strategic Framing · Generator
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            Cascade Mapper
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 720, margin: 0 }}>
            Most people freeze when shocks hit because they only see the first-order
            impact. The asymmetric move is mapping the cascade — what ends, what
            becomes valuable, who wins, who loses, what reverts. Customized to YOUR
            specific career/business/portfolio/family exposure.
          </p>
        </div>

        {/* Input form */}
        <div style={{ padding: '28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 24 }}>
          <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 22px 0' }}>
            The Shock + Your Exposure
          </h3>

          <Field label="The shock or event">
            <textarea
              value={shock}
              onChange={(e) => setShock(e.target.value)}
              rows={4}
              placeholder="Describe the shock specifically. Example: 'Fed announces emergency rate cuts due to banking sector liquidity crisis. Three regional banks failed in 48 hours.'"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <Field label="Time frame (optional)" hint="When does this play out? 'Next 90 days' or 'multi-year'">
            <input type="text" value={timeFrame} onChange={(e) => setTimeFrame(e.target.value)} style={inputStyle} placeholder="e.g., next 6-18 months" />
          </Field>

          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--sp-divider)' }}>
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
              Your Personal Exposure (any combination)
            </div>

            <Field label="Career" hint="Industry, role, employer concentration">
              <input type="text" value={careerExposure} onChange={(e) => setCareerExposure(e.target.value)} style={inputStyle} placeholder="e.g., Senior eng at mid-size SaaS company" />
            </Field>

            <Field label="Business" hint="If you own/run one">
              <input type="text" value={businessExposure} onChange={(e) => setBusinessExposure(e.target.value)} style={inputStyle} placeholder="e.g., $2M ARR B2B startup, 15 employees" />
            </Field>

            <Field label="Portfolio" hint="Major holdings or concentrations">
              <input type="text" value={portfolioExposure} onChange={(e) => setPortfolioExposure(e.target.value)} style={inputStyle} placeholder="e.g., 60% tech equities, 20% real estate, 20% cash" />
            </Field>

            <Field label="Family" hint="Dependents, geography, risk profile">
              <input type="text" value={familyExposure} onChange={(e) => setFamilyExposure(e.target.value)} style={inputStyle} placeholder="e.g., 2 kids, mortgage in Bay Area, single income" />
            </Field>
          </div>

          <div style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={generate}
              disabled={loading || shock.trim().length < 10}
              style={{
                padding: '12px 24px',
                background: loading || shock.trim().length < 10 ? 'transparent' : 'var(--sp-brass-bg)',
                border: `1px solid ${loading || shock.trim().length < 10 ? 'var(--sp-divider)' : 'var(--sp-aged-brass)'}`,
                color: loading || shock.trim().length < 10 ? 'rgba(232, 221, 196, 0.40)' : 'var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.16em',
                cursor: loading || shock.trim().length < 10 ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Mapping cascade...' : 'Map Cascade →'}
            </button>
            {error && <div style={{ fontSize: 12, color: 'var(--sp-oxblood)', fontFamily: 'var(--sp-font-mono)' }}>{error}</div>}
          </div>
        </div>

        {loading && (
          <div style={{ padding: '48px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', textAlign: 'center', fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', color: 'var(--sp-aged-brass)', fontSize: 16 }}>
            Mapping the cascade...
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontStyle: 'normal', fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 8, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              7 visible layers · personalized to your exposure
            </div>
          </div>
        )}

        {result && !loading && (
          <div>
            {/* Shock summary */}
            <div style={{ padding: '28px', background: 'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)', border: '1px solid var(--sp-aged-brass)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 12 }}>
                Shock Framing
              </div>
              <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 16, lineHeight: 1.6, color: 'var(--sp-warm-cream)', marginBottom: 16 }}>
                {result.shockSummary}
              </div>
              <div style={{ paddingTop: 14, borderTop: '1px solid var(--sp-divider)' }}>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--sp-aged-brass)', marginBottom: 6 }}>
                  Your Exposure Assessment
                </div>
                <div style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 14, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.85)' }}>
                  {result.userExposureAssessment}
                </div>
              </div>
            </div>

            {/* Cascade layers — 2-column grid */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', borderTop: '1px solid var(--sp-aged-brass)', paddingTop: 12, marginBottom: 14 }}>
                The Cascade
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>
                {(result.layers || []).map((layer) => (
                  <LayerCard key={layer.num} layer={layer} />
                ))}
              </div>
            </div>

            {/* Permanence */}
            <div style={{ padding: '24px 28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 12 }}>
                Permanent vs Temporary
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.85)' }}>
                {result.permanenceAssessment}
              </div>
            </div>

            {/* Timing horizon — 4 column grid */}
            {result.timingHorizon && (
              <div style={{ padding: '24px 28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
                  Timing Horizon
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                  <TimingCell label="Days" content={result.timingHorizon.days} />
                  <TimingCell label="Weeks" content={result.timingHorizon.weeks} />
                  <TimingCell label="Months" content={result.timingHorizon.months} />
                  <TimingCell label="Years" content={result.timingHorizon.years} />
                </div>
              </div>
            )}

            {/* Personal actions */}
            <div style={{ padding: '24px 28px', background: 'rgba(58, 84, 71, 0.12)', border: '1px solid rgba(58, 84, 71, 0.40)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-forest-moss)', marginBottom: 14 }}>
                Your Personal Actions
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(result.personalActions || []).map((a, i) => (
                  <PersonalActionCard key={i} action={a} />
                ))}
              </div>
            </div>

            {/* Honest read */}
            <div style={{ padding: '20px 24px', background: 'rgba(74, 31, 31, 0.10)', border: '1px solid rgba(74, 31, 31, 0.30)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--sp-oxblood)', marginBottom: 10 }}>
                Honest Read
              </div>
              <div style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 14, lineHeight: 1.65, color: 'var(--sp-warm-cream)' }}>
                {result.honestRead}
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232, 221, 196, 0.45)', marginRight: 8 }}>
                Connected:
              </span>
              <Link href="/strategic-playbook/library?type=shock-playbook" style={{ color: 'var(--sp-aged-brass)' }}>
                All 8 shock playbooks
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LayerCard({ layer }) {
  const accent = LAYER_ACCENTS[layer.icon] || 'var(--sp-aged-brass)';

  return (
    <div style={{ padding: '20px 22px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: accent, opacity: 0.7 }} />
      <div style={{ paddingLeft: 12 }}>
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: accent, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>
          Layer {layer.num}
        </div>
        <h4 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 16, color: 'var(--sp-warm-cream)', margin: '0 0 12px 0' }}>
          {layer.title}
        </h4>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {(layer.items || []).map((item, i) => (
            <li key={i} style={{ fontSize: 12, lineHeight: 1.55, color: 'rgba(232, 221, 196, 0.78)', marginBottom: 5 }}>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TimingCell({ label, content }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'var(--sp-aged-brass)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.55, color: 'rgba(232, 221, 196, 0.78)' }}>
        {content}
      </div>
    </div>
  );
}

function PersonalActionCard({ action }) {
  const urgencyColor = URGENCY_COLORS[action.urgency] || 'var(--sp-aged-brass)';
  return (
    <div style={{ padding: '14px 18px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ minWidth: 90 }}>
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'var(--sp-aged-brass)', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
          {action.category}
        </div>
        <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, padding: '2px 7px', border: `1px solid ${urgencyColor}`, borderRadius: 'var(--sp-radius)', color: urgencyColor, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
          {action.urgency}
        </span>
      </div>
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.85)' }}>
        {action.action}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 6 }}>
        {label}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 4, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  outline: 'none',
};

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Cascade Mapper</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
